import React from 'react';
import { Spinner } from 'react-bootstrap';
import { INK } from 'Utils/styleTokens';
import { RISK_BG_COLORS, RISK_TEXT_COLORS, TYPE_COLORS, TYPE_LABELS } from './constants';

const SchemaIdentityCard = ({
  identity,
  selected,
  maxSizeGB,
  onSelect,
  runningJobs,
  onCancelJob,
  cancelling,
}) => {
  const bw = maxSizeGB > 0 ? Math.round((identity.sizeGB / maxSizeGB) * 100) : 0;
  const uniqueTypes = [...new Set(identity.types || [])];
  const regionCount = identity.regions ? identity.regions.length : identity.regionCount;
  // Two-line label: datasource name on top, database name italic underneath.
  // Falls back to the cluster's displayName if the representative member isn't enriched yet.
  const primaryName = identity.primaryName || identity.name;
  const primaryDatabase = identity.primaryDatabase || null;
  const extraInstances = identity.instances && identity.instances > 1
    ? identity.instances - 1
    : 0;
  const jobs = runningJobs || [];
  // Last profiled, shown at the far left so the list reads chronologically under the
  // default sort. Never-profiled identities say so rather than rendering a bare dash,
  // which is indistinguishable from "we failed to load a date".
  const lastProfiledLabel = (() => {
    const t = identity.latestSnapshotAt;
    if (!t) return null;
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return null;
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString(undefined, sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: '2-digit' });
  })();

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(identity.id);
    }
  };

  return (
    <div
      className={'metadata-card' + (selected ? ' selected' : '')}
      data-testid='schema-identity-card'
      data-instances={identity.instances != null ? identity.instances : 1}
      onClick={() => onSelect(identity.id)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role='button'
      aria-pressed={selected}
      aria-label={`Select ${primaryName}, ${identity.risk} risk`}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 5 }}>
        <div
          data-testid='schema-identity-last-profiled'
          title={identity.latestSnapshotAt
            ? `Last profiled ${new Date(identity.latestSnapshotAt).toLocaleString()}`
            : 'Never profiled'}
          style={{
            flex: '0 0 auto',
            width: 52,
            fontSize: 11,
            lineHeight: '16px',
            color: lastProfiledLabel ? INK[500] : INK[300],
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {lastProfiledLabel || 'never'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: '0 1 auto',
              }}
            >
              {primaryName}
            </span>
            {extraInstances > 0 && (
              <span
                className='metadata-more-chip'
                style={{
                  fontSize: 10,
                  color: INK[500],
                  background: INK[100],
                  borderRadius: 8,
                  padding: '1px 6px',
                  whiteSpace: 'nowrap',
                }}
              >
                +{extraInstances} more
              </span>
            )}
          </div>
          {primaryDatabase && (
            <div
              className='metadata-card-line2'
              style={{
                fontSize: 11,
                fontStyle: 'italic',
                color: INK[400],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginTop: 1,
              }}
            >
              {primaryDatabase}
            </div>
          )}
        </div>
        <span
          className='metadata-rbadge'
          style={{
            background: RISK_BG_COLORS[identity.risk],
            color: RISK_TEXT_COLORS[identity.risk],
          }}
        >
          {identity.risk}
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 7, alignItems: 'center' }}>
        {uniqueTypes.map((t) => {
          const n = identity.types.filter((x) => x === t).length;
          const tc = TYPE_COLORS[t];
          if (!tc) return null;
          return (
            <span
              key={t}
              className='metadata-tpill'
              style={{ background: tc.bg, color: tc.text }}
            >
              {n > 1 ? `${n}× ` : ''}{TYPE_LABELS[t] || t}
            </span>
          );
        })}
        {regionCount != null && (
          <span style={{ marginLeft: 'auto', fontSize: 10, color: INK[300] }}>
            {regionCount} region{regionCount === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div
        style={{
          background: INK[150],
          height: 3,
          borderRadius: 2,
          overflow: 'hidden',
          marginBottom: 6,
        }}
      >
        <div
          style={{
            width: `${bw}%`,
            height: '100%',
            background: INK[400],
            opacity: 0.6,
            borderRadius: 2,
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: 5, fontSize: 11, color: INK[400] }}>
        <span>{identity.sizeGB}GB</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>{identity.tables} tables</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>{identity.rowsM}M rows</span>
      </div>
      {/* In-flight profile runs for this identity's sources render inside the card —
          the card IS the status surface, no separate panel. */}
      {jobs.map((j) => (
        <div
          key={j.id}
          data-testid='card-running-job'
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7, fontSize: 11, color: INK[500] }}
        >
          <Spinner animation='border' size='sm' style={{ width: 12, height: 12, color: INK[400] }} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {j.status === 'PENDING' ? 'Profile queued' : 'Profiling'}
            {jobs.length > 1 || identity.instances > 1
              ? ` — ${j.dataSourceName || `source ${j.dataSourceId}`}`
              : '…'}
          </span>
          {onCancelJob && (
            <button
              type='button'
              className='metadata-sortbtn'
              data-testid='card-running-job-cancel'
              onClick={(e) => { e.stopPropagation(); onCancelJob(j.id); }}
              disabled={!!(cancelling && cancelling[j.id])}
              style={{ fontSize: 10, padding: '1px 8px' }}
            >
              {cancelling && cancelling[j.id] ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default SchemaIdentityCard;
